import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";

import { arrivalOptions } from "../../../lib/rateCardUtils";
import { ARRIVAL } from "../arrival";
import { LOCAL_DATETIME, toLocalInput as toLocal } from "../departure";

/**
 * When the guest says they will turn up.
 */
export function initialMode(initial, options, requireExact = false) {
  if (initial?.arrivalMode) return initial.arrivalMode;
  if (initial?.expectedArrival) return ARRIVAL.SCHEDULED;
  if (options.allowNow) return ARRIVAL.NOW;
  /* The desk now has to say when the guest turns up, so a card that lets one
   * be picked never falls back to the card's own entry time. */
  if (requireExact && options.allowScheduled) return ARRIVAL.SCHEDULED;

  return ARRIVAL.DEFAULT;
}

export default function useArrival({
  rateCard,
  tz,
  initial,
  startDay,
  setStartDay,
  endDay,
  setEndDay,
  startDateTime,
  endDateTime,
  isEdit,
  departure,
  requireExact = false,
}) {
  const options = useMemo(() => arrivalOptions(rateCard), [rateCard]);

  const [mode, setMode] = useState(() => initialMode(initial, options, requireExact));
  const [at, setAtState] = useState(() => toLocal(initial?.expectedArrival, tz));

  const seededAt = useRef(Boolean(at));

  useEffect(() => {
    if (seededAt.current || !tz || !initial?.expectedArrival) return;

    seededAt.current = true;
    setAtState(toLocal(initial.expectedArrival, tz));
  }, [tz, initial]);

  /* Keep the selection legal when the operator switches guest card. */
  useEffect(() => {
    if (!rateCard) return;

    setMode((current) => {
      if (current === ARRIVAL.SCHEDULED && options.allowScheduled) return current;
      if (current === ARRIVAL.NOW && options.allowNow) return current;
      if (requireExact && options.allowScheduled) return ARRIVAL.SCHEDULED;
      if (options.allowDefault) return ARRIVAL.DEFAULT;
      /* No card is left without a default today, but a card that offered
       * nothing else still has to land somewhere legal. */
      return options.allowNow ? ARRIVAL.NOW : ARRIVAL.DEFAULT;
    });
  }, [rateCard, options, requireExact]);

  useEffect(() => {
    if (mode !== ARRIVAL.SCHEDULED || !at) return;

    /* datetime-local is already wall-clock in the facility's zone. */
    const day = at.slice(0, 10);
    if (day.length !== 10 || day === startDay) return;

    setStartDay(day);
  }, [mode, at, startDay, setStartDay]);

  const exitFollowsArrival =
    rateCard?.type === "HOURLY" || rateCard?.type === "TIERED_HOURS";

  /* An hourly card sells time, not a slot, so its window opens at midnight.
   * Accepting the default there means the desk does not know when the guest
   * turns up - the mirror of the departure's own "Mark unknown" - and
   * labelling the tile with the window's start would quote 00:00 as an entry
   * nobody chose. */
  const hasCardEntry = !exitFollowsArrival;

  const exitCanFollow = Boolean(
    exitFollowsArrival ||
      departure?.hasMovableExitDate ||
      departure?.exitDerivedFromEntry,
  );

  /* Moving the arrival onto another day pushes the departure ahead of it
   * rather than being blocked by it. */
  const setAt = (next) => {
    setAtState(next);
    if (exitCanFollow) departure?.followArrival(at, next);
  };

  const setDay = (nextDay) => {
    setStartDay(nextDay);
    if (exitCanFollow && startDay) departure?.followArrival(startDay, nextDay);
  };

  useEffect(() => {
    if (!exitFollowsArrival || !startDay) return;
    if (endDay === startDay) return;

    setEndDay(startDay);
  }, [exitFollowsArrival, startDay, endDay, setEndDay]);

  /* Open the picker on something plausible: the entry the card already sells
   * where it has one, otherwise the next hour for a walk-up. Never in the
   * past, which is the one thing the picker will not accept back. */
  const seedAt = () => {
    if (at || !tz) return;
    /* The booking's own arrival is still on its way in; seeding now would
     * overwrite it a render later. */
    if (initial?.expectedArrival && !seededAt.current) return;

    const soonest = DateTime.now().setZone(tz).plus({ hours: 1 }).startOf("hour");
    const carded =
      options.allowDefault && startDateTime
        ? DateTime.fromISO(startDateTime, { zone: tz })
        : null;

    const seed = carded?.isValid && carded > soonest ? carded : soonest;

    if (seed.isValid)
      setAtState((current) => current || seed.toFormat(LOCAL_DATETIME));
  };

  /* The card may have moved the mode here without anyone pressing a tile. */
  useEffect(() => {
    if (mode !== ARRIVAL.SCHEDULED) return;

    seedAt();
  });

  const expectedArrivalAt = useMemo(() => {
    if (mode !== ARRIVAL.SCHEDULED || !at || !tz) return null;

    const dt = DateTime.fromISO(at, { zone: tz });
    return dt.isValid ? dt.toISO() : null;
  }, [mode, at, tz]);

  const bounds = useMemo(() => {
    if (!tz) return {};

    /* A booking cannot be made to have started already. An existing one may
     * legitimately sit in the past, so editing keeps its floor open. */
    const min = isEdit
      ? undefined
      : DateTime.now().setZone(tz).toFormat(LOCAL_DATETIME);

    const end = endDateTime ? DateTime.fromISO(endDateTime, { zone: tz }) : null;

    return {
      min,
      max:
        !exitCanFollow && end?.isValid
          ? end.toFormat(LOCAL_DATETIME)
          : undefined,
    };
  }, [tz, endDateTime, isEdit, exitCanFollow]);

  const outOfRange = Boolean(
    mode === ARRIVAL.SCHEDULED &&
      at &&
      ((bounds.max && at > bounds.max) || (bounds.min && at < bounds.min)),
  );

  const valid =
    mode !== ARRIVAL.SCHEDULED || (Boolean(expectedArrivalAt) && !outOfRange);

  /* The entry the card itself sells, for the default tile's label. */
  const scheduledEntryLabel = useMemo(() => {
    if (!startDateTime || !tz) return null;

    const dt = DateTime.fromISO(startDateTime, { zone: tz });
    return dt.isValid ? dt.toFormat("HH:mm, ccc d LLL") : null;
  }, [startDateTime, tz]);

  const tileLabel = useMemo(() => {
    if (!startDateTime || !tz) return null;

    const dt = DateTime.fromISO(startDateTime, { zone: tz });
    if (!dt.isValid) return null;

    const days = dt
      .startOf("day")
      .diff(DateTime.now().setZone(tz).startOf("day"), "days").days;

    if (days === 0) return `From ${dt.toFormat("HH:mm")} today`;
    if (days === 1) return `From ${dt.toFormat("HH:mm")} tomorrow`;

    return `From ${dt.toFormat("HH:mm, ccc d LLL")}`;
  }, [startDateTime, tz]);

  const tiles = useMemo(() => {
    const list = [];

    if (options.allowNow) {
      list.push({
        id: ARRIVAL.NOW,
        label: "Now",
        hint: "Guest is here at the desk",
      });
    }

    if (options.allowDefault) {
      list.push({
        id: ARRIVAL.DEFAULT,
        label: hasCardEntry ? tileLabel ?? "Scheduled entry" : "Pick date",
        hint: hasCardEntry
          ? "Entry time on this guest card"
          : "Guest does not know time yet",
      });
    }

    if (options.allowScheduled) {
      list.push({
        id: ARRIVAL.SCHEDULED,
        label: "Pick date & time",
        hint: "Guest knows exact arrival",
      });
    }

    return list;
  }, [options, tileLabel, hasCardEntry]);

  const select = (next) => {
    setMode(next);

    if (next !== ARRIVAL.SCHEDULED) return;

    seedAt();
  };

  return {
    mode,
    at,
    setAt,
    setDay,
    select,
    options,
    tiles,
    bounds,
    expectedArrivalAt,
    scheduledEntryLabel,
    outOfRange,
    valid,
  };
}
